'use client';

import { useCallback, useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import clsx from 'clsx';
import './index.scss';

import { useScreenSize } from '@/hooks/useScreenSize';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { NoResultsFound } from '@/components/PaginationList/NoResultsFound';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import { DEFAULT_SEARCH_PARAMS } from '@/constants/insights';
import { SwiperNavButton } from '../Swipers/SwiperNavButton';
import { SwiperNavArrowColor, SwiperNavArrowOrientation } from '@/images/svg/SwiperNavArrow';
import { PREV_BLOG_QUERY, PREV_CS_QUERY } from '@/constants/localStorage';

interface IListItemsPerPage {
  desktop: number;
  tablet: number;
  mobile: number;
}

interface IListItemProps {
  id: string;
}

interface IPaginationListProps<T extends IListItemProps> {
  list: T[];
  activeTags?: string[] | null;
  listItemComponent: React.ComponentType<T>;
  listItemsPerPage: IListItemsPerPage;
  tabsSelectorId: string;
  listClassName: string;
  listItemClassName?: string;
  activeLinkBgCLassName?: string;
  swiperArrow?: boolean;
  storageParam: string;
}

export const PaginationList = <T extends IListItemProps>({
  list,
  activeTags,
  listItemComponent: ListItemComponent,
  listItemsPerPage,
  tabsSelectorId,
  listClassName,
  listItemClassName,
  activeLinkBgCLassName = 'bg-white',
  swiperArrow = false,
  storageParam,
}: IPaginationListProps<T>) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const [initialPage, setInitialPage] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(listItemsPerPage.desktop);
  const { width } = useScreenSize();
  const [loading, setLoading] = useState(true);

  const getItemsPerPage = useCallback(
    (currentWidth: number) => {
      let returnItemsPerPage = listItemsPerPage.desktop;

      if (currentWidth < BREAKPOINTS['md-1'] && itemsPerPage !== listItemsPerPage.mobile) {
        returnItemsPerPage = listItemsPerPage.mobile;
      }

      if (
        currentWidth < BREAKPOINTS['xl-1'] &&
        currentWidth >= BREAKPOINTS['md-1'] &&
        itemsPerPage !== listItemsPerPage.tablet
      ) {
        returnItemsPerPage = listItemsPerPage.tablet;
      }

      if (currentWidth >= BREAKPOINTS['xl-1'] && itemsPerPage !== listItemsPerPage.desktop) {
        returnItemsPerPage = listItemsPerPage.desktop;
      }

      return returnItemsPerPage;
    },
    [listItemsPerPage],
  );

  useEffect(() => {
    setItemsPerPage(getItemsPerPage(width));
  }, [getItemsPerPage, width, itemsPerPage]);

  useEffect(() => {
    setLoading(true);
    const pageSearchParam = searchParams.get(DEFAULT_SEARCH_PARAMS.page) || 1;
    const initialPageParam = Number(pageSearchParam) - 1;
    if (pageSearchParam) {
      setLoading(false);
    }
    const windowWidth = window.innerWidth;
    const initialItemsPerPage = getItemsPerPage(windowWidth);
    setInitialPage(initialPageParam);
    const newOffset = (initialPageParam * initialItemsPerPage) % list.length;
    setItemOffset(newOffset);
  }, []);

  const endOffset = itemOffset + itemsPerPage;
  const currentItems = list.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(list.length / itemsPerPage);
  const currentPage = Math.ceil((itemOffset + 1) / itemsPerPage);

  const handlePageClick = (event: { selected: number }) => {
    const params = new URLSearchParams(searchParams);
    params.set(DEFAULT_SEARCH_PARAMS.page, (event.selected + 1).toString());
    const url = `${pathname}?${params.toString()}`;
    router.replace(`${url}`, {
      scroll: false,
    });
    const newOffset = (event.selected * itemsPerPage) % list.length;
    setItemOffset(newOffset);
  };

  const handleItemClick = () => {
    localStorage?.removeItem(PREV_CS_QUERY);
    localStorage?.removeItem(PREV_BLOG_QUERY);
    const urlSearchParams = searchParams.toString();
    if (urlSearchParams) {
      localStorage?.setItem(storageParam, urlSearchParams);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="loading-screen"
        className="h-screen flex justify-center items-center bg-white"
      ></div>
    );
  }

  return (
    <div
      data-testid="pagination-list"
      className="flex flex-col gap-[64px] flashLoad min-h-[600px]"
    >
      {list.length ? (
        <ul
          data-testid="pagination-items-list"
          className={clsx('grid', {
            [`${listClassName}`]: listClassName,
          })}
        >
          {currentItems.map((item) => {
            return (
              <li
                key={item.id}
                data-testid={`pagination-item-${item.id}`}
                className={clsx({
                  [`${listItemClassName}`]: listItemClassName,
                })}
              >
                <ListItemComponent
                  {...item}
                  activeTags={activeTags}
                  onClick={handleItemClick}
                />
              </li>
            );
          })}
        </ul>
      ) : (
        <NoResultsFound data-testid="no-results" />
      )}

      <div className="relative">
        <ReactPaginate
          forcePage={list.length ? initialPage : undefined}
          breakLabel="..."
          onPageChange={handlePageClick}
          pageRangeDisplayed={3}
          pageCount={pageCount}
          activeLinkClassName={clsx('pointer-events-none', {
            [`${activeLinkBgCLassName}`]: activeLinkBgCLassName,
          })}
          renderOnZeroPageCount={null}
          containerClassName={clsx(
            `w-full flex py-[20px] px-0 lg:px-[16px] xl:px-[32px] [@media(min-width:1440px)]:px-[20px]
            border-t-[1px] border-black-4/60 md-tablet:border-grey-1 gap-[5px]`,
          )}
          disabledClassName="opacity-50 pointer-events-none"
          nextClassName={clsx('ml-auto', {
            ['relative mr-[-16px] lg:mr-0 xl:mr-[-16px] right-[1%] mobile:right-[2%] lg:right-[9%]']: swiperArrow,
          })}
          previousClassName={clsx('mr-auto', {
            ['relative ml-[-16px] lg:ml-0 xl:ml-[-16px] left-[1%] mobile:left-[2%] lg:left-[9%]']: swiperArrow,
          })}
          pageLinkClassName="
            w-[40px] h-[40px] rounded-full hover:bg-purple-1 flex justify-center items-center text-[14px]
            md-tablet:flex hidden
          "
          breakLinkClassName="pointer-events-none"
          breakClassName="md-tablet:block hidden"
          previousLabel={
            <SwiperNavButton
              orientation={SwiperNavArrowOrientation.LEFT}
              color={SwiperNavArrowColor.PINK}
              className="relative"
              testId="previous-button"
              name="Swipe left"
            />
          }
          nextLabel={
            <SwiperNavButton
              orientation={SwiperNavArrowOrientation.RIGHT}
              color={SwiperNavArrowColor.PINK}
              className="relative"
              testId="next-button"
              name="Swipe right"
            />
          }
          onClick={({ isActive }) => {
            if (!isActive) {
              document.querySelector(`#${tabsSelectorId}`)?.scrollIntoView({ block: 'start' });
            }
          }}
        />

        {pageCount !== 0 && (
          <div
            data-testid="pagination-info"
            className="
            absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%]
            md-tablet:invisible text-black-8 text-sm leading-[20px]
          "
          >
            Page {currentPage} of {pageCount}
          </div>
        )}
      </div>
    </div>
  );
};
